import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Heart, Shield, MapPin, Calendar, Stethoscope,
  ArrowRight, Sparkles, Pill, Activity, Baby,
  ChevronRight,
} from "lucide-react";

const Landing = ({ user }) => {
  const navigate = useNavigate();

  const handleGetStarted = () => navigate(user ? "/chat" : "/login");

  const suggestions = [
    { icon: Stethoscope, label: "I have a headache and fever", color: "#10b981" },
    { icon: Pill,        label: "Drug interactions",           color: "#6366f1" },
    { icon: Baby,        label: "Child vaccination schedule",  color: "#f59e0b" },
    { icon: Activity,    label: "Blood pressure readings",     color: "#ef4444" },
  ];

  const features = [
    { icon: Stethoscope, title: "Symptom Analysis",   desc: "Describe how you feel and get an instant AI triage." },
    { icon: MapPin,      title: "Nearby Care",         desc: "Hospitals, pharmacies & labs around you in seconds." },
    { icon: Calendar,    title: "Appointments",        desc: "Book or manage visits without phone calls." },
    { icon: Shield,      title: "Private & Secure",    desc: "Your health data stays encrypted and never sold." },
  ];

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#212121", color: "#ececec", fontFamily: "'Inter', sans-serif" }}>

      {/* ── Navbar ── */}
      <nav style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "16px 32px", borderBottom: "1px solid #2f2f2f",
        position: "sticky", top: 0, zIndex: 50, backdropFilter: "blur(12px)",
        backgroundColor: "rgba(33,33,33,0.85)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 34, height: 34, borderRadius: 8,
            background: "linear-gradient(135deg,#10b981,#0ea5e9)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Heart size={18} color="white" />
          </div>
          <span style={{ fontWeight: 700, fontSize: 18, color: "#fff", letterSpacing: "-0.4px" }}>SupaDoc</span>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          {user ? (
            <motion.button
              whileHover={{ backgroundColor: "#10b981" }}
              onClick={() => navigate("/chat")}
              style={{
                padding: "8px 18px", borderRadius: 8, border: "none", cursor: "pointer",
                backgroundColor: "#10b981", color: "#fff", fontWeight: 600, fontSize: 14,
                display: "flex", alignItems: "center", gap: 6,
              }}
            >
              Open Chat <ArrowRight size={14} />
            </motion.button>
          ) : (
            <>
              <motion.button
                whileHover={{ backgroundColor: "#2f2f2f" }}
                onClick={() => navigate("/login")}
                style={{
                  padding: "8px 18px", borderRadius: 8, border: "1px solid #3f3f3f",
                  cursor: "pointer", backgroundColor: "transparent", color: "#ececec",
                  fontWeight: 500, fontSize: 14,
                }}
              >
                Log in
              </motion.button>
              <motion.button
                whileHover={{ opacity: 0.9 }}
                onClick={() => navigate("/login")}
                style={{
                  padding: "8px 18px", borderRadius: 8, border: "none", cursor: "pointer",
                  background: "linear-gradient(135deg,#10b981,#0ea5e9)",
                  color: "#fff", fontWeight: 600, fontSize: 14,
                  display: "flex", alignItems: "center", gap: 6,
                }}
              >
                Get started <ChevronRight size={14} />
              </motion.button>
            </>
          )}
        </div>
      </nav>

      {/* ── Hero ── */}
      <section style={{ maxWidth: 720, margin: "0 auto", padding: "96px 24px 64px", textAlign: "center" }}>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Badge */}
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            backgroundColor: "#2a2a2a", border: "1px solid #3a3a3a",
            borderRadius: 99, padding: "5px 14px", marginBottom: 32,
            fontSize: 13, color: "#a0a0a0",
          }}>
            <Sparkles size={13} color="#10b981" />
            <span>Powered by advanced AI</span>
          </div>

          {/* Headline */}
          <h1 style={{
            fontSize: "clamp(2rem, 5vw, 3.2rem)", fontWeight: 700,
            lineHeight: 1.15, letterSpacing: "-0.03em", color: "#fff",
            marginBottom: 20,
          }}>
            Your personal<br />
            <span style={{ color: "#10b981" }}>AI medical assistant</span>
          </h1>

          <p style={{ fontSize: 17, color: "#8e8ea0", lineHeight: 1.7, maxWidth: 520, margin: "0 auto 40px" }}>
            Describe your symptoms, understand your health, find nearby care — all in one conversation.
          </p>

          {/* Fake chat input — clicking opens chat */}
          <motion.div
            whileHover={{ borderColor: "#10b981" }}
            onClick={handleGetStarted}
            style={{
              display: "flex", alignItems: "center", gap: 12,
              backgroundColor: "#2f2f2f", border: "1.5px solid #3f3f3f",
              borderRadius: 16, padding: "14px 18px", cursor: "text",
              textAlign: "left", transition: "border-color 0.2s", marginBottom: 28,
            }}
          >
            <Stethoscope size={18} color="#6b7280" />
            <span style={{ flex: 1, color: "#6b7280", fontSize: 15 }}>
              Message SupaDoc…
            </span>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: "linear-gradient(135deg,#10b981,#0ea5e9)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <ArrowRight size={16} color="white" />
            </div>
          </motion.div>

          {/* Suggestion chips */}
          <div style={{
            display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center",
          }}>
            {suggestions.map((s, i) => (
              <motion.button
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 + i * 0.07 }}
                whileHover={{ backgroundColor: "#2f2f2f", borderColor: s.color }}
                onClick={handleGetStarted}
                style={{
                  display: "flex", alignItems: "center", gap: 7,
                  padding: "8px 14px", borderRadius: 99,
                  backgroundColor: "#262626", border: `1px solid #3a3a3a`,
                  cursor: "pointer", color: "#c0c0c0", fontSize: 13, fontWeight: 500,
                  transition: "all 0.2s",
                }}
              >
                <s.icon size={13} color={s.color} />
                {s.label}
              </motion.button>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ── Features ── */}
      <section style={{ maxWidth: 960, margin: "0 auto", padding: "40px 24px 80px" }}>
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 16,
        }}>
          {features.map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              style={{
                backgroundColor: "#2a2a2a", border: "1px solid #333",
                borderRadius: 16, padding: "24px 20px",
              }}
            >
              <div style={{
                width: 40, height: 40, borderRadius: 10,
                backgroundColor: "#1e1e1e", border: "1px solid #3a3a3a",
                display: "flex", alignItems: "center", justifyContent: "center",
                marginBottom: 14,
              }}>
                <f.icon size={18} color="#10b981" />
              </div>
              <div style={{ fontWeight: 600, fontSize: 15, color: "#e0e0e0", marginBottom: 6 }}>{f.title}</div>
              <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.6 }}>{f.desc}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── Pricing strip ── */}
      <section style={{
        borderTop: "1px solid #2f2f2f", borderBottom: "1px solid #2f2f2f",
        backgroundColor: "#1c1c1c", padding: "60px 24px",
      }}>
        <div style={{ maxWidth: 680, margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ fontSize: 26, fontWeight: 700, color: "#fff", marginBottom: 10, letterSpacing: "-0.02em" }}>
            Start with 10 free messages
          </h2>
          <p style={{ color: "#6b7280", fontSize: 15, marginBottom: 36 }}>
            No credit card needed. Upgrade anytime for unlimited consultations.
          </p>
          <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
            {[
              { name: "Free",    price: "₹0",   sub: "10 messages/month",      highlight: false },
              { name: "Starter", price: "₹199", sub: "200 messages/month",     highlight: false },
              { name: "Pro",     price: "₹499", sub: "Unlimited messages",      highlight: true  },
            ].map((p, i) => (
              <motion.div
                key={i}
                whileHover={{ borderColor: "#10b981" }}
                style={{
                  backgroundColor: p.highlight ? "#1a2e23" : "#262626",
                  border: `1.5px solid ${p.highlight ? "#10b981" : "#333"}`,
                  borderRadius: 16, padding: "24px 28px",
                  minWidth: 170, textAlign: "left",
                  transition: "border-color 0.2s", position: "relative",
                }}
              >
                {p.highlight && (
                  <span style={{
                    position: "absolute", top: -11, left: "50%", transform: "translateX(-50%)",
                    backgroundColor: "#10b981", color: "#fff",
                    fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 99,
                    letterSpacing: "0.04em", textTransform: "uppercase",
                  }}>Popular</span>
                )}
                <div style={{ color: "#a0a0a0", fontSize: 13, marginBottom: 6 }}>{p.name}</div>
                <div style={{ fontSize: 26, fontWeight: 700, color: p.highlight ? "#10b981" : "#e0e0e0", marginBottom: 4 }}>{p.price}</div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>{p.sub}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Footer CTA ── */}
      <section style={{ padding: "72px 24px", textAlign: "center" }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 style={{ fontSize: 28, fontWeight: 700, color: "#fff", marginBottom: 12, letterSpacing: "-0.02em" }}>
            Ready to talk to your AI doctor?
          </h2>
          <p style={{ color: "#6b7280", fontSize: 15, marginBottom: 28 }}>
            Available 24/7. No waiting rooms.
          </p>
          <motion.button
            whileHover={{ opacity: 0.88, scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={handleGetStarted}
            data-testid="cta-btn"
            style={{
              padding: "13px 32px", borderRadius: 10, border: "none", cursor: "pointer",
              background: "linear-gradient(135deg,#10b981,#0ea5e9)",
              color: "#fff", fontWeight: 600, fontSize: 15,
              display: "inline-flex", alignItems: "center", gap: 8,
            }}
          >
            Get started free <ArrowRight size={16} />
          </motion.button>
        </motion.div>
      </section>

      {/* ── Footer bar ── */}
      <footer style={{
        borderTop: "1px solid #2a2a2a", padding: "20px 32px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 10,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Heart size={14} color="#10b981" />
          <span style={{ fontSize: 13, color: "#4b5563" }}>© 2026 SupaAid Healthtech Pvt. Ltd.</span>
        </div>
        <div style={{ display: "flex", gap: 20 }}>
          {["Privacy", "Terms", "Contact"].map((l) => (
            <span key={l} style={{ fontSize: 13, color: "#4b5563", cursor: "pointer" }}>{l}</span>
          ))}
        </div>
      </footer>
    </div>
  );
};

export default Landing;
